import type { TeamTypeFilter } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { USER_ROLES } from "@nlc/shared-types";
import { useState } from "react";

import type { UserRole } from "@/features/auth/permissions/config";

import { FeaturePageLayout } from "@/features/shared/layout";
import { useDebounce } from "@/hooks/useDebounce";
import { getPageSizeOptions } from "@/utils/getPageSizeOptions";

import { TeamsTable } from "./components/TeamsTable";
import { useGetTeamsCounts } from "./shared/hooks/useGetTeamsCounts";
import { useListTeams } from "./shared/hooks/useListTeams.tsx";

export function ListTeamsPage() {
  const [inputValue, setInputValue] = useState("");
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "createdAt",
    direction: "descending",
  });
  const [selectedTeamType, setSelectedTeamType] =
    useState<TeamTypeFilter>("all");

  const debouncedQuery = useDebounce(inputValue, 500, () => setPage(1));

  const direction: "asc" | "desc" =
    sortDescriptor.direction === "descending" ? "desc" : "asc";

  const { data: response, isFetching } = useListTeams({
    direction,
    itemsPerPage,
    page,
    search: debouncedQuery,
    sortBy: sortDescriptor?.column as
      | "activeTeamMembers"
      | "assignedAccounts"
      | "createdAt"
      | "designLead"
      | "memberCount"
      | "name",
    teamType: selectedTeamType,
  });

  const { data: countsData } = useGetTeamsCounts();

  const teams = response?.teams || [];
  const metadata = response?.metadata;

  const handleTeamTypeChange = (type: TeamTypeFilter) => {
    setSelectedTeamType(type);
    setPage(1); // Reset to first page when filter changes
  };

  const teamTypeTabs = [
    { countLabel: countsData?.all || 0, id: "all", label: "All" },
    { countLabel: countsData?.graphic || 0, id: "graphic", label: "Graphic" },
    { countLabel: countsData?.video || 0, id: "video", label: "Video" },
    { countLabel: countsData?.website || 0, id: "website", label: "Website" },
    { countLabel: countsData?.brand || 0, id: "brand", label: "Brand" },
    {
      countLabel: countsData?.videoEditing || 0,
      id: "video_editing",
      label: "Video Editing",
    },
    { countLabel: countsData?.other || 0, id: "others", label: "Others" },
  ];

  return (
    <div className="p-xl">
      <FeaturePageLayout
        createButton={{
          href: "/teams/create",
          label: "New Team",
          requiredRoles: [
            USER_ROLES.SUPER_ADMIN,
            USER_ROLES.TEAM_LEADER,
            USER_ROLES.ADMIN,
          ] as UserRole[],
        }}
        isLoading={isFetching}
        searchBar={{
          inputValue,
          setInputValue,
        }}
        tabs={{
          onSelectionChange: (key) => {
            handleTeamTypeChange(key as TeamTypeFilter);
          },
          selectedKey: selectedTeamType,
          tabs: teamTypeTabs,
        }}
        title="Teams"
      >
        <TeamsTable
          isLoading={isFetching}
          pagination={
            metadata
              ? {
                  metadata: {
                    currentPage: metadata.currentPage,
                    itemsPerPage: metadata.itemsPerPage,
                    totalItems: metadata.totalItems,
                    totalPages: metadata.totalPages,
                  },
                  onItemsPerPageChange: (newItemsPerPage) => {
                    setItemsPerPage(newItemsPerPage);
                    setPage(1);
                  },
                  onPageChange: setPage,
                  pageSizeOptions: getPageSizeOptions(metadata.totalItems),
                  showPageSizeSelector: true,
                }
              : undefined
          }
          setSortDescriptor={setSortDescriptor}
          sortDescriptor={sortDescriptor}
          teams={teams}
        />
      </FeaturePageLayout>
    </div>
  );
}
